import { Component, EventEmitter, HostBinding, Input, OnChanges, Output, SimpleChanges } from '@angular/core'
import { Deliverable, DeliveryStatus } from '@app/features/scoping/models/deliverable.model'
import { trackById } from '@app/shared/utils/utils'
import { Dictionary, groupBy, isNil } from 'lodash'
import {
  ScopeElementsConfig,
  UpdateDeliverablePayload,
  UpdateFeeItemPayload,
  UpdateRolePayload,
  UpdateTpcPayload,
} from '../models'

import { FormControl } from '@angular/forms'
import { CdnConfig } from '@app/core/model/cdn-config.model'
import { MenuOptions } from '@app/core/model/definitions/menu-options.interface'
import { ApplicationSetting } from '@app/core/model/enums/application-setting.enum'
import { Privilege } from '@app/core/model/enums/privilege.enum'
import { StatusType } from '@app/core/model/scope-version'
import { ThirdPartyCost } from '@app/core/model/third-party-cost.model'
import { User } from '@app/core/model/user.model'
import { AuthService } from '@app/core/service/auth.service'
import { ApprovalFlowService } from '@app/features/scope-overview/service/approval-flow.service'
import { ScopeTabService } from '@app/features/scope-overview/service/scope-tab.service'
import { ScopeComponent } from '@app/features/scoping/models/component.model'
import { isPreferenceActive } from '@app/shared/components/ui-components/scope-ui-table/table-column-preferences.type'
import { plainToInstance } from 'class-transformer'
import { DescriptionUpdate } from '../../description-editor/description-editor.component'
import { ScopeElementsService } from '../services/scope-elements.service'
import { getThirdPartyCostColumns } from '../utils'
import { hasFixedCostDeliverableEditPrivileges } from '../utils/has-fixed-cost-edit-privileges.util'
import { ChangeItem, ChangeType } from '@app/features/scope-overview/change-request/model'
import { UpdateComponentPayload } from '../models/update-component-payload.interface'
import {
  getCrFieldToDisplay,
  isChangeItemAdded,
  isChangeItemRemoved,
  showCrDifferences,
  getCrNetChanges,
  showCrNetChanges,
} from '../utils/change-request'
import { DeliverableWithChanges } from '../models/entities-with-changes/deliverable-with-changes.interface'
import { ConvertCurrencyPipe } from '@app/shared/pipe/convertCurrency.pipe'
import { FormatHoursPipe, formatHoursPipeFn } from '@app/shared/pipe/formatHours.pipe'
import { DEFAULT_PLACEHOLDER } from '@app/shared/constants/default-placeholder.const'
import { formatCrPrice, formatPercentage } from '../utils/format-value.utils'
import { Money } from '@app/features/scope-overview/model/money.model'

@Component({
  selector: 'scope-deliverables-row',
  templateUrl: './deliverables-row.component.html',
  styleUrls: ['./deliverables-row.component.scss'],
  providers: [FormatHoursPipe],
})
export class DeliverablesRowComponent implements OnChanges {
  @Input() deliverable: DeliverableWithChanges
  @Input() config: ScopeElementsConfig
  @Input() level: number
  @Input() cdnConfig: CdnConfig
  @Input() changeItem: ChangeItem

  @Input() deliverableMenuOptions: MenuOptions[]
  @Input() componentMenuOptions: MenuOptions[]
  @Input() feeMenuOptions: MenuOptions[]

  @Output() onExpandDeliverable = new EventEmitter<number>()
  @Output() onUpdateDeliverable = new EventEmitter<UpdateDeliverablePayload>()
  @Output() onAcceptDeliverable = new EventEmitter<Deliverable>()
  @Output() onRejectDeliverable = new EventEmitter<Deliverable>()

  @Output() onUpdateComponent = new EventEmitter<UpdateComponentPayload>()
  @Output() onOverrideComponentQuantity = new EventEmitter<ScopeComponent>()
  @Output() onOverrideRateCardRoleHours = new EventEmitter<UpdateRolePayload>()
  @Output() onMoveComponent = new EventEmitter<{ component: ScopeComponent; deliverable: Deliverable }>()
  @Output() onMoveComponentOutsideDeliverable = new EventEmitter<ScopeComponent>()
  @Output() onUpdateFeeItem = new EventEmitter<UpdateFeeItemPayload>()
  @Output() onUpdateRole = new EventEmitter<UpdateRolePayload>()
  @Output() onUpdateDeliverableTpc = new EventEmitter<UpdateTpcPayload>()
  @Output() onUpdateComponentTpc = new EventEmitter<UpdateTpcPayload>()
  @Output() onUpdateDeliverableSectionTpc = new EventEmitter<UpdateTpcPayload>()
  @Output() onReorderDeliverableTpcs = new EventEmitter<{
    deliverable: Deliverable
    thirdPartyCosts: ThirdPartyCost[]
  }>()
  @Output() onReorderComponentTpcs = new EventEmitter<any>()
  @Output() onReorderDeliverableSectionTpcs = new EventEmitter<any>()

  componentsMap: Dictionary<ScopeComponent[]> = {}
  deliveryStatus: any
  isEditAgencyPrice = false
  agencyPriceControl = new FormControl()

  get currentUser(): User {
    return this.authService.loggedInUser
  }

  get isTraded(): boolean {
    return this.deliverable.deliveryStatus === DeliveryStatus.TRADED
  }

  get isTradingInProgress(): boolean {
    return (
      this.deliverable.deliveryStatus === DeliveryStatus.TRADING_FROM_IN_PROGRESS ||
      this.deliverable.deliveryStatus === DeliveryStatus.TRADING_TO_IN_PROGRESS
    )
  }

  get isEditable(): boolean {
    return this.config?.isEditable && (!this.deliverable.fixedPricing || this.deliverable.fixedCostEditable)
  }

  get isAgencyPriceEditable(): boolean {
    return (
      this.isEditable &&
      this.currentUser.hasPrivilege(Privilege.SCOPE_MARKUP__EDIT) &&
      (!this.deliverable.fixedPricing ||
        (hasFixedCostDeliverableEditPrivileges(this.currentUser, this.config?.scopeVersion) &&
          this.deliverable.fixedCostEditable))
    )
  }

  get updatedConfig(): ScopeElementsConfig {
    return { ...this.config, isDeliverableEditable: this.isEditable }
  }

  constructor(
    private authService: AuthService,
    private scopeTabService: ScopeTabService,
    protected scopeElementsService: ScopeElementsService,
    protected scopeApprovalFlowService: ApprovalFlowService,
    private convertCurrencyPipe: ConvertCurrencyPipe
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.deliverable && this.deliverable) {
      this.componentsMap = groupBy(this.deliverable.components, (component) => component.section?.id)
      this.deliveryStatus = this.getDeliveryStatusDetails()
    }
  }

  fetchDeliverable($event) {
    $event?.preventDefault()
    $event?.stopPropagation()
    this.scopeElementsService.toggleDeliverable(this.deliverable.id)
    if (this.scopeElementsService.deliverableStates.selected[this.deliverable.id])
      this.onExpandDeliverable.emit(this.deliverable.id)
  }

  updateDeliverable(updatedFields: Partial<Deliverable>) {
    const changes = Object.keys(updatedFields).reduce((acc, key) => {
      const change = this.changeItem?.data?.[key]
      acc[key] = {
        old: !isNil(change) ? change.old : this.deliverable[key],
        new: updatedFields[key],
      }
      return acc
    }, {})

    this.onUpdateDeliverable.emit({
      originalDeliverable: this.deliverable,
      deliverable: plainToInstance(Deliverable, { ...this.deliverable, components: null, ...updatedFields }),
      changes,
    })
  }

  saveDeliverableExtraDescription($event: DescriptionUpdate) {
    this.updateDeliverable({ description: $event.main, extraDescription: $event.extra })
  }

  editAgencyPrice($event: MouseEvent) {
    this.preventDefault($event)
    this.agencyPriceControl.setValue(this.deliverable.getUnitSellingPrice()?.amount)
    this.isEditAgencyPrice = true
  }

  preventDefault(event: MouseEvent) {
    event.preventDefault()
    event.stopPropagation()
  }

  updateAgencyPrice(amount: number) {
    this.isEditAgencyPrice = false

    if (amount !== this.deliverable.getUnitSellingPrice()?.amount) {
      if (isNil(amount)) return this.updateDeliverable({ fixedCostOffset: null })

      this.deliverable.recalculateFixedCostOffset(amount)

      this.updateDeliverable({ fixedCostOffset: this.deliverable.fixedCostOffset })
    }
  }

  getTpcColumns = () =>
    getThirdPartyCostColumns(this.config?.isEditable, (thirdPartyCost: ThirdPartyCost) =>
      this.onUpdateDeliverableTpc.emit({ deliverable: this.deliverable, thirdPartyCost })
    )

  updateDeliverableTpc(thirdPartyCost: ThirdPartyCost) {
    this.onUpdateDeliverableTpc.emit({ deliverable: this.deliverable, thirdPartyCost })
  }

  reorderDeliverableTpcs(thirdPartyCosts: ThirdPartyCost[]) {
    this.onReorderDeliverableTpcs.emit({ deliverable: this.deliverable, thirdPartyCosts })
  }

  getCommentsCount = (): number => this.scopeTabService.scopeCommentsData?.[this.deliverable.id]

  getDeliveryStatusDetails() {
    switch (this.deliverable.deliveryStatus) {
      case DeliveryStatus.ON_HOLD:
        return { class: 'red', name: 'ON HOLD' }
      case DeliveryStatus.IN_PROGRESS:
        return { class: 'green', name: 'IN PROGRESS' }
      case DeliveryStatus.COMPLETE:
        return { class: 'yellow', name: 'COMPLETE' }
      case DeliveryStatus.TRADED:
        return { class: 'grey', name: 'TRADED', tooltip: 'This {LANG:deliverable|l} has been traded' }
      case DeliveryStatus.TRADING_FROM_IN_PROGRESS:
        return {
          class: 'red',
          name: 'TRADING FROM',
          tooltip: 'This {LANG:deliverable|l} is being traded; awaiting confirmation.',
        }
      case DeliveryStatus.TRADING_TO_IN_PROGRESS:
        return {
          class: 'red',
          name: 'TRADING TO',
          tooltip: `This {LANG:deliverable|l} is being traded for '${this.deliverable.tradedFrom?.name}'. Edit the {LANG:deliverable|l} before confirming trade.`,
        }
      default:
        return { class: 'ultra-grey', name: 'N/A' }
    }
  }

  showToggleMenu() {
    if (!(this.deliverableMenuOptions || []).filter((option) => !option.isHidden?.(this.deliverable)).length)
      return false

    if (this.config?.scopeVersion.isStateApproved() && this.config?.isChangeRequest) return true

    if (this.scopeApprovalFlowService.isScopeEditable(this.config?.scopeVersion)) return true

    if (
      this.deliverable.isPendingTrade() &&
      this.config?.scopeVersion.findDeliverableById(this.deliverable.tradedFrom.id).tradesCount > 1
    )
      return true

    if (
      this.deliverable.deliveryStatus == DeliveryStatus.IN_PROGRESS &&
      this.config?.scopeVersion.isStateApproved() &&
      this.scopeApprovalFlowService.stateChecks.deliverableStatus.isApprover &&
      this.scopeApprovalFlowService.stateChecks.deliverableStatus.isStateApproved
    )
      return (
        this.currentUser.hasPrivilege(Privilege.SCOPE_DELIVERABLE__COMPLETE) ||
        (this.currentUser.company.hasApplicationSetting(ApplicationSetting.SCOPE__DELIVERABLE__TRADING) &&
          this.currentUser.hasPrivilege(Privilege.SCOPE_DELIVERABLE__TRADE))
      )

    if (this.deliverable.isBeingTraded())
      return (
        this.currentUser.company.hasApplicationSetting(ApplicationSetting.SCOPE__DELIVERABLE__TRADING) &&
        this.currentUser.hasPrivilege(Privilege.SCOPE_DELIVERABLE__TRADE)
      )

    if (
      this.deliverable.deliveryStatus == DeliveryStatus.ON_HOLD &&
      this.scopeApprovalFlowService.stateChecks.deliverableStatus.isStateApproved &&
      this.scopeApprovalFlowService.stateChecks.deliverableStatus.isApprover
    )
      return this.currentUser.hasPrivilege(Privilege.SCOPE_DELIVERABLE__STOP)

    return false
  }

  getDeliverableWithDescriptions(): Deliverable {
    return plainToInstance(Deliverable, {
      ...this.deliverable,
      description: this.getFieldToDisplay('description'),
      extraDescription: this.getFieldToDisplay('extraDescription'),
      internalNote: this.getFieldToDisplay('internalNote'),
    })
  }

  isEmptyDeliverable(): boolean {
    return (
      !this.componentsMap['undefined']?.length &&
      !this.deliverable.sections?.length &&
      !this.deliverable.feeItems?.length &&
      !this.deliverable.thirdPartyCosts?.length
    )
  }

  getFieldToDisplay = (fieldName: string) => getCrFieldToDisplay<DeliverableWithChanges>(fieldName, this.deliverable)
  showDiff = (fieldName: string): boolean =>
    showCrDifferences<DeliverableWithChanges>(this.config, this.deliverable, fieldName)

  formatPrice = (value: number | Money): string => formatCrPrice(value, this.config?.currency, this.convertCurrencyPipe)
  protected readonly formatHours = formatHoursPipeFn

  showNetChanges = (fieldName: string) =>
    showCrNetChanges(this.config, this.isElementAdded || this.isElementRemoved, this.deliverable.changes?.[fieldName])
  getNetChanges = (fieldName: string, defaultValue: number) =>
    getCrNetChanges(this.deliverable.changes, fieldName, defaultValue, this.isElementAdded, this.isElementRemoved)

  @HostBinding('class.change-request-removed') get isElementRemoved() {
    return isChangeItemRemoved(this.config, this.changeItem)
  }
  @HostBinding('class.change-request-added') get isElementAdded() {
    return isChangeItemAdded(this.config, this.changeItem, this.deliverable)
  }

  protected readonly Privilege = Privilege
  protected readonly ApplicationSetting = ApplicationSetting
  protected readonly StatusType = StatusType
  protected readonly trackById = trackById
  protected readonly isPreferenceActive = isPreferenceActive
  protected readonly isNil = isNil
  protected readonly ChangeType = ChangeType
  protected readonly formatPercentage = formatPercentage
  protected readonly DEFAULT_PLACEHOLDER = DEFAULT_PLACEHOLDER
}
